/**
 * AUTH BREADCRUMBS (PR-F0 A3, 2026-09-21 — Miles: "some oddities with getting
 * logged out"). DIAGNOSTICS ONLY — no behaviour changes here.
 *
 * The 01:08Z sign-out on /desktop left no trace on the client: the request log
 * showed a page load, a `/signup` navigation while signed in, two 401s and a fresh
 * Google sign-in, and nothing said WHICH client branch signed the person out
 * (the explicit logout, or one of the identity-link flow's "no email from the
 * provider" branches) or why the app opened `/signup`. So the three client
 * sign-out doors record their branch, the moment and the route into ONE
 * localStorage key the next report is read from, plus a console line; the signup
 * page records when it mounts for an already signed-in person. Never throws,
 * never blocks a sign-out, stores no credential, no email and no content.
 */
export const SIGN_OUT_BREADCRUMB_KEY = 'prest0_auth_last_signout';
export const SIGNUP_WHILE_SIGNED_IN_KEY = 'prest0_auth_signup_while_signed_in';

export type SignOutBreadcrumbReason =
  | 'explicit_logout'
  | 'google_popup_no_email'
  | 'google_credential_no_email';

export interface AuthBreadcrumb {
  at: string;
  reason: string;
  route: string;
}

export interface BreadcrumbStorageLike {
  setItem(key: string, value: string): void;
  getItem(key: string): string | null;
}

export function buildAuthBreadcrumb(reason: string, nowMs: number, route: string): AuthBreadcrumb {
  const at = Number.isFinite(nowMs) && nowMs > 0 ? new Date(nowMs).toISOString() : '';
  return { at, reason: String(reason || ''), route: String(route || '') };
}

function defaultRoute(): string {
  try {
    return typeof window !== 'undefined' && window.location ? String(window.location.pathname || '') : '';
  } catch {
    return '';
  }
}

function defaultStorage(): BreadcrumbStorageLike | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    return null;
  }
}

function record(
  key: string,
  reason: string,
  opts: { storage?: BreadcrumbStorageLike | null; nowMs?: number; route?: string } = {},
): AuthBreadcrumb {
  const crumb = buildAuthBreadcrumb(
    reason,
    typeof opts.nowMs === 'number' ? opts.nowMs : Date.now(),
    typeof opts.route === 'string' ? opts.route : defaultRoute(),
  );
  const storage = opts.storage === undefined ? defaultStorage() : opts.storage;
  try {
    storage?.setItem(key, JSON.stringify(crumb));
  } catch {
    /* storage may be absent or blocked — the console line still lands */
  }
  try {
    // eslint-disable-next-line no-console
    console.warn(`[auth] ${key}`, crumb);
  } catch {
    /* never throw from a diagnostic */
  }
  return crumb;
}

/** One of the three client sign-out doors fired. */
export function recordSignOutBreadcrumb(
  reason: SignOutBreadcrumbReason,
  opts?: { storage?: BreadcrumbStorageLike | null; nowMs?: number; route?: string },
): AuthBreadcrumb {
  return record(SIGN_OUT_BREADCRUMB_KEY, reason, opts);
}

/** The signup page mounted for a person who is already signed in (not anonymous). */
export function recordSignupWhileSignedIn(
  opts?: { storage?: BreadcrumbStorageLike | null; nowMs?: number; route?: string },
): AuthBreadcrumb {
  return record(SIGNUP_WHILE_SIGNED_IN_KEY, 'signup_mounted_while_signed_in', opts);
}

export function readAuthBreadcrumb(key: string, storage: BreadcrumbStorageLike | null = defaultStorage()): AuthBreadcrumb | null {
  try {
    const raw = storage?.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AuthBreadcrumb>;
    if (!parsed || typeof parsed !== 'object') return null;
    return { at: String(parsed.at || ''), reason: String(parsed.reason || ''), route: String(parsed.route || '') };
  } catch {
    return null;
  }
}
